import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const BarSetup = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [staff, setStaff] = useState([]);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    staff_name: '',
    role: 'bartender',
    phone: '',
    email: '',
    shift: 'evening'
  });
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/admin/bar/setup/staff');
      if (data.success) setStaff(data.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
    setLoading(false);
  };

  const handleSubmitStaff = async (e) => {
    e.preventDefault();
    try {
      const url = editingStaff
        ? `${PRODUCTION_URL}/api/admin/bar/setup/staff/${editingStaff.id}`
        : `${PRODUCTION_URL}/api/admin/bar/setup/staff`;

      const response = await fetch(url, {
        method: editingStaff ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(staffForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Staff member ${editingStaff ? 'updated' : 'added'} successfully!`);
        setShowStaffModal(false);
        setEditingStaff(null);
        resetForm();
        fetchStaff();
      }
    } catch (error) {
      console.error('Error saving staff:', error);
    }
  };

  const handleEditStaff = (member) => {
    setEditingStaff(member);
    setStaffForm({
      staff_name: member.staff_name,
      role: member.role,
      phone: member.phone || '',
      email: member.email || '',
      shift: member.shift
    });
    setShowStaffModal(true);
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/bar/setup/staff/${staffId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Staff member deleted successfully!');
        fetchStaff();
      }
    } catch (error) {
      console.error('Error deleting staff:', error);
    }
  };

  const toggleStaffStatus = async (staffId, currentStatus) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/bar/setup/staff/${staffId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Staff member ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
        fetchStaff();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const resetForm = () => {
    setStaffForm({
      staff_name: '',
      role: 'bartender',
      phone: '',
      email: '',
      shift: 'evening'
    });
  };

  const getRoleColor = (role) => {
    const roleColors = {
      head_bartender: colors.primary,
      bartender: colors.accent,
      bar_back: colors.warning,
      server: colors.success
    };
    return roleColors[role] || colors.textLight;
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      head_bartender: 'Head Bartender',
      bartender: 'Bartender',
      bar_back: 'Bar Back',
      server: 'Server'
    };
    return roleLabels[role] || role;
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'space-between'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      background: colors.white
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading bar setup...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const activeStaff = staff.filter(s => s.is_active).length;
  const headBartenders = staff.filter(s => s.role === 'head_bartender').length;
  const bartenders = staff.filter(s => s.role === 'bartender').length;
  const totalDrinksMade = staff.reduce((sum, s) => sum + (s.drinks_made || 0), 0);

  return (
    <AdminLayout>
      {successMessage && (
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          background: 'rgba(74, 124, 42, 0.15)',
          color: colors.success,
          border: `2px solid ${colors.success}`,
          margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
        }}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Bar Setup</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Bar configuration' : 'Configure bar settings, staff, and operational parameters'}
        </p>
      </div>

      {/* Statistics */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.primary }}>
            {staff.length}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Total Staff
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.success}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.success }}>
            {activeStaff}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Active Staff
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.accent }}>
            {bartenders}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Bartenders
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.warning}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.warning }}>
            {totalDrinksMade}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Drinks Today
          </div>
        </div>
      </div>

      {/* Staff Management */}
      <div style={styles.contentCard}>
        <div style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>👥</span>
            <span>Bar Staff</span>
          </div>
          <button
            onClick={() => {
              setEditingStaff(null);
              resetForm();
              setShowStaffModal(true);
            }}
            style={{
              ...styles.button,
              background: colors.primary,
              color: colors.white
            }}
          >
            + Add Staff
          </button>
        </div>

        {staff.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No staff members found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Name</th>
                  <th style={styles.tableHeader}>Role</th>
                  {!isMobile && <th style={styles.tableHeader}>Phone</th>}
                  <th style={styles.tableHeader}>Shift</th>
                  {!isMobile && <th style={styles.tableHeader}>Today</th>}
                  <th style={styles.tableHeader}>Status</th>
                  <th style={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map(member => (
                  <tr key={member.id} style={{ opacity: member.is_active ? 1 : 0.6 }}>
                    <td style={styles.tableCell}>
                      <strong>{member.staff_name}</strong>
                      {isMobile && (
                        <div style={{ fontSize: '11px', color: colors.textLight }}>
                          {member.phone}
                        </div>
                      )}
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: `${getRoleColor(member.role)}20`,
                        color: getRoleColor(member.role)
                      }}>
                        {getRoleLabel(member.role)}
                      </span>
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{member.phone || 'N/A'}</td>
                    )}
                    <td style={styles.tableCell}>
                      <span style={{ textTransform: 'capitalize' }}>{member.shift}</span>
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>
                        <strong>{member.drinks_made || 0}</strong> drinks
                      </td>
                    )}
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => toggleStaffStatus(member.id, member.is_active)}
                        style={{
                          ...styles.button,
                          background: member.is_active ? colors.success : colors.danger,
                          color: colors.white,
                          fontSize: '11px',
                          padding: '6px 12px'
                        }}
                      >
                        {member.is_active ? '✓ Active' : '✕ Inactive'}
                      </button>
                    </td>
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => handleEditStaff(member)}
                        style={{
                          ...styles.button,
                          background: colors.accent,
                          color: colors.white,
                          fontSize: '11px',
                          padding: '6px 12px',
                          marginRight: '6px'
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDeleteStaff(member.id)}
                        style={{
                          ...styles.button,
                          background: colors.danger,
                          color: colors.white,
                          fontSize: '11px',
                          padding: '6px 12px'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Staff Modal */}
      {showStaffModal && (
        <div style={styles.modal} onClick={() => setShowStaffModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
            </h3>

            <form onSubmit={handleSubmitStaff}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Staff Name *</label>
                <input
                  type="text"
                  value={staffForm.staff_name}
                  onChange={(e) => setStaffForm({ ...staffForm, staff_name: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Role *</label>
                  <select
                    value={staffForm.role}
                    onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
                    style={styles.select}
                    required
                  >
                    <option value="bartender">Bartender</option>
                    <option value="head_bartender">Head Bartender</option>
                    <option value="bar_back">Bar Back</option>
                    <option value="server">Server</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Shift *</label>
                  <select
                    value={staffForm.shift}
                    onChange={(e) => setStaffForm({ ...staffForm, shift: e.target.value })}
                    style={styles.select}
                    required
                  >
                    <option value="morning">Morning</option>
                    <option value="evening">Evening</option>
                    <option value="night">Night</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  value={staffForm.phone}
                  onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                  style={styles.input}
                  placeholder="e.g., 9876543210"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={staffForm.email}
                  onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                  style={styles.input}
                  placeholder="e.g., staff@hotel.com"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    background: colors.primary,
                    color: colors.white,
                    flex: 1
                  }}
                >
                  {editingStaff ? 'Update Staff' : 'Add Staff'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowStaffModal(false)}
                  style={{
                    ...styles.button,
                    background: colors.mediumGray,
                    color: colors.textDark,
                    flex: 1
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default BarSetup;